import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { fr, it, uk, nl, enGB, de } from "date-fns/locale";
import { useTranslation } from "react-i18next";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import InterBold from "@robin/common/inter/Inter-SemiBold.woff";
import InterMedium from "@robin/common/inter/Inter-Medium.woff2";
import InterRegular from "@robin/common/inter/Inter-Regular.woff2";
import { createMuiTheme } from "@robin/common";

import AppContextProvider from "./context/AppContext";
import { AppRoutes } from "./AppRoutes";
import { DatabaseProvider } from "./context/DatabaseContext";
import { FlushQueriesOnLogout } from "./components/layout/FlushQueriesOnLogout";
import { LoginContextProvider } from "./context/LoginContext";
import { dbCreation } from "./db/db-creation";
import SnackbarContextProvider from "./context/SnackbarContext";

const localeMap = {
  de: de,
  en: enGB,
  fr: fr,
  it: it,
  nl: nl,
  uk: uk,
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {},
  },
});

const theme = createMuiTheme({
  color: "blue",
  mode: "light",
  target: "desktop",
  interFont: {
    InterBold,
    InterMedium,
    InterRegular,
  },
});

export const App: React.FC = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const currentLocale =
    localeMap[currentLanguage as keyof typeof localeMap] || de;
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <QueryClientProvider client={queryClient}>
        <DatabaseProvider dbName={"robin-view"} upgradeDb={dbCreation}>
          <LoginContextProvider>
            <AppContextProvider>
              <SnackbarContextProvider>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={currentLocale}
                >
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <AppRoutes />
                  </ThemeProvider>
                </LocalizationProvider>
              </SnackbarContextProvider>
            </AppContextProvider>
          </LoginContextProvider>
        </DatabaseProvider>
        <FlushQueriesOnLogout />
      </QueryClientProvider>
    </BrowserRouter>
  );
};
